import React, { useState, useEffect, useRef } from 'react';
import { X, Building, Check, AlertCircle, MapPin, Search, Crosshair } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;


export const DispensarySubmissionModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    website: '',
    latitude: '',
    longitude: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const { colors } = useTheme();
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load Google Maps API
  useEffect(() => {
    if (!window.google && !window.googleMapsLoading) {
      window.googleMapsLoading = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsMapLoaded(true);
        window.googleMapsLoading = false;
      };
      document.head.appendChild(script);
    } else if (window.google) {
      setIsMapLoaded(true);
    }
  }, []);

  // Initialize map when modal opens and Google Maps is loaded
  useEffect(() => {
    if (isOpen && isMapLoaded && mapRef.current && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [isOpen, isMapLoaded]);

  const initializeMap = () => {
    if (!window.google || !mapRef.current) return;

    // Default to San Francisco if no coordinates
      const defaultCenter = { lat: 42.3646296, lng: -71.0615132 };
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: defaultCenter,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Add click listener to map
    mapInstanceRef.current.addListener('click', (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      updateMapMarker(lat, lng);
      reverseGeocode(lat, lng);
    });

    // Initialize autocomplete for search
    if (searchInputRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          types: ['establishment'],
          fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          // Auto-fill form with place data
          setFormData(prev => ({
            ...prev,
            name: prev.name || place.name || '',
            address: getAddressComponent(place, 'street_number') + ' ' + getAddressComponent(place, 'route'),
            city: getAddressComponent(place, 'locality'),
            state: getAddressComponent(place, 'administrative_area_level_1'),
            postal_code: getAddressComponent(place, 'postal_code'),
            latitude: lat.toString(),
            longitude: lng.toString()
          }));

          updateMapMarker(lat, lng);
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(16);
        }
      });
    }
  };

  const getAddressComponent = (place, type) => {
    const component = place.address_components?.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
  };

  const updateMapMarker = (lat, lng) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      draggable: true,
      title: 'Dispensary Location'
    });

    // Update coordinates when marker is dragged
    markerRef.current.addListener('dragend', (event) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      setFormData(prev => ({
        ...prev,
        latitude: newLat.toString(),
        longitude: newLng.toString()
      }));
      reverseGeocode(newLat, newLng);
    });

    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const reverseGeocode = (lat, lng) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const result = results[0];
        const addressComponents = result.address_components;
        
        setFormData(prev => ({
          ...prev,
          address: getComponentFromResult(addressComponents, 'street_number') + ' ' + 
                  getComponentFromResult(addressComponents, 'route'),
          city: getComponentFromResult(addressComponents, 'locality'),
          state: getComponentFromResult(addressComponents, 'administrative_area_level_1'),
          postal_code: getComponentFromResult(addressComponents, 'postal_code')
        }));
      }
    });
  };

  const getComponentFromResult = (components, type) => {
    const component = components.find(comp => comp.types.includes(type));
    return component ? component.long_name : '';
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        updateMapMarker(lat, lng);
        reverseGeocode(lat, lng);
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({ lat, lng });
          mapInstanceRef.current.setZoom(16);
        }
        
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your current location. Please select manually on the map.');
        setIsLoadingLocation(false);
      }
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Update map marker when coordinates change
    if ((field === 'latitude' || field === 'longitude') && value && mapInstanceRef.current) {
      const lat = field === 'latitude' ? parseFloat(value) : parseFloat(formData.latitude);
      const lng = field === 'longitude' ? parseFloat(value) : parseFloat(formData.longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        updateMapMarker(lat, lng);
        mapInstanceRef.current.setCenter({ lat, lng });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Dispensary name is required';
    }
    
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Please enter a valid website URL (include http:// or https://)';
    }
    
    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      newErrors.latitude = 'Latitude must be a number between -90 and 90';
    }
    
    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      newErrors.longitude = 'Longitude must be a number between -180 and 180';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };
      
      await onSubmit(submitData);
      setSubmitStatus('success');
      
      setTimeout(() => {
        setFormData({
          name: '',
          address: '',
          city: '',
          state: '',
          postal_code: '',
          website: '',
          latitude: '',
          longitude: ''
        });
        setSubmitStatus(null);
        onClose();
      }, 2000);
      
    } catch (error) {
      setSubmitStatus('error');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        website: '',
        latitude: '',
        longitude: ''
      });
      setErrors({});
      setSubmitStatus(null);
      
      // Clean up map
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          background:colors.bgGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Building style={{ color: '#00d4aa' }} size={24} />
              Submit Dispensary
            </h2>
            <p style={{
              margin: '4px 0 0 36px',
              color: '#888',
              fontSize: '14px'
            }}>
              Use the map to find and mark the dispensary location
            </p>
          </div>
          
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#888';
              }
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Status */}
        {submitStatus && (
          <div style={{
            padding: '16px 24px',
            background: submitStatus === 'success' 
              ? 'rgba(0, 212, 170, 0.1)' 
              : 'rgba(239, 68, 68, 0.1)',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {submitStatus === 'success' ? (
              <>
                <Check size={20} style={{ color: '#00d4aa' }} />
                <span style={{ color: '#00d4aa', fontWeight: '500' }}>
                  Dispensary submitted successfully! It will be reviewed by our team.
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={20} style={{ color: '#ef4444' }} />
                <span style={{ color: '#ef4444', fontWeight: '500' }}>
                  Failed to submit dispensary. Please try again.
                </span>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          height: 'calc(90vh - 200px)',
          overflow: 'hidden'
        }}>
          {/* Left Panel - Form */}
          <div style={{
            padding: '24px',
            overflowY: 'auto',
            borderRight: '1px solid #333'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Map Search */}
              <div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MapPin style={{ color: '#00d4aa' }} size={18} />
                  Find Location
                </h3>
                
                <div style={{ marginBottom: '12px' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for dispensary or address..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
                  />
                </div>

                <button
                  onClick={getCurrentLocation}
                  disabled={isLoadingLocation}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: isLoadingLocation 
                      ? '#666' 
                      : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '500',
                    cursor: isLoadingLocation ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  <Crosshair size={16} />
                  {isLoadingLocation ? 'Getting Location...' : 'Use Current Location'}
                </button>
              </div>

              {/* Required Section */}
              <div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                    color: '#0a0a0a',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    REQUIRED
                  </span>
                  Basic Information
                </h3>
                
                {/* Dispensary Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5'
                  }}>
                    Dispensary Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter dispensary name"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: `2px solid ${errors.name ? '#ef4444' : '#333'}`,
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      if (!errors.name) e.currentTarget.style.borderColor = '#00d4aa';
                    }}
                    onBlur={(e) => {
                      if (!errors.name) e.currentTarget.style.borderColor = '#333';
                    }}
                  />
                  {errors.name && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {errors.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Location Details */}
              <div>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#e5e5e5'
                }}>
                  Location Details
                </h3>

                {/* Address */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5'
                  }}>
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="123 Main Street"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
                  />
                </div>

                {/* City, State, ZIP */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 80px 80px', 
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                    style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="CA"
                    maxLength="2"
                    style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    placeholder="90210"
                    style={{
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Website */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#e5e5e5'
                  }}>
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://dispensary.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#2a2a2a',
                      border: `2px solid ${errors.website ? '#ef4444' : '#333'}`,
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {errors.website && (
                    <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {errors.website}
                    </span>
                  )}
                </div>

                {/* Coordinates */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '12px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5'
                    }}>
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => handleInputChange('latitude', e.target.value)}
                      placeholder="34.0522"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        border: `2px solid ${errors.latitude ? '#ef4444' : '#333'}`,
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#e5e5e5'
                    }}>
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => handleInputChange('longitude', e.target.value)}
                      placeholder="-118.2437"
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#2a2a2a',
                        border: `2px solid ${errors.longitude ? '#ef4444' : '#333'}`,
                        borderRadius: '8px',
                        color: '#e5e5e5',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: '1px solid #333'
            }}>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  padding: '12px 24px',
                  background: '#333',
                  color: '#e5e5e5',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '500',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.name.trim()}
                style={{
                  padding: '12px 24px',
                  background: (isSubmitting || !formData.name.trim()) 
                    ? '#666' 
                    : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                  color: (isSubmitting || !formData.name.trim()) ? '#ccc' : '#0a0a0a',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: (isSubmitting || !formData.name.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ccc',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Building size={16} />
                    Submit Dispensary
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Panel - Map */}
          <div style={{ position: 'relative', background: '#2a2a2a' }}>
            {!isMapLoaded ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#888'
              }}>
                Loading map...
              </div>
            ) : (
              <>
                <div
                  ref={mapRef}
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  Click on map to set location
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};